// `reports.api.ng1.ts` — the full-page Reports dialog (`/chrome-extension/reports`).
//
// Every shape below was exercised against a live tenant on 7 Oct rather than read off the
// upstream interface, because two of them are wrong in the upstream interface.
import { request } from './request.ts'
import { fetchFile, type DownloadedFile } from './download.ts'

const REPORTS = '/chrome-extension-api/reports'

export interface ProductNameResponse {
  productName: string
}

/**
 * ⚠️ `toTimestamp` IS REQUIRED AND `fromTimestamp` IS NOT. Upstream's form marks the "To"
 * field `required` and deletes `fromTimestamp` from the request when the field is cleared, so
 * "no from date" is an ABSENT KEY rather than a null — the CSV filename branches on exactly
 * that (`until <to>` vs `<from> to <to>`).
 */
export interface UsageReportRequest {
  fromTimestamp?: number
  toTimestamp: number
  department?: string
  activeUsersOnly: boolean
}

/**
 * The `Extended` row. Upstream types it as `Omit<…, 'candidatesMessageOpened' |
 * 'candidatesMessageResponded'>`, which reads as though the server replaced those counts with
 * the two rates. It did not: measured on 7 Oct, the wire carries ALL ELEVEN fields — both raw
 * counts AND both rates. The `Omit` is a modelling choice, because the table renders only the
 * rates, and it is kept here for the same reason rather than because the counts are absent.
 *
 * Every numeric cell is still guarded with an `isNaN` check upstream and must be here too: a
 * missing rate renders "undefined%" otherwise, and these rows come from an aggregate query
 * that can legitimately produce nulls.
 */
export interface UserUsageReport {
  name: string
  email: string
  department: string
  huntersCreated: number
  candidatesViewed: number
  candidatesExported: number
  candidatesMessaged: number
  candidatesMessageOpenedRate: number
  candidatesMessageRespondedRate: number
}

export interface UsageReportResponse {
  users: UserUsageReport[]
  /** A row, not a scalar — it carries its own `name`/`email`/`department` strings. */
  total: UserUsageReport
}

/**
 * ⚠️ THE THIRD FIELD IS `…Last180Days`, AND UPSTREAM'S INTERFACE SAYS `…Last18Days`.
 *
 * `reports.api.ng1.ts` declares `totalCandidateEnrichedLast18Days`; the template renders
 * `ctrl.report.totalCandidateEnrichedLast180Days` under the label "Last 6 Months". Only one
 * of them can be the server's. Measured on 7 Oct, the tenant answers:
 *
 *     {"totalCandidateEnriched":0,"totalCandidateEnrichedLast90Days":0,
 *      "totalCandidateEnrichedLast180Days":0}
 *
 * So the TEMPLATE is right and the interface's `Last18Days` is a dead typo — nothing reads
 * it, which is why it survived. 180 days is also the only reading consistent with the label.
 */
export interface EnrichmentReportResponse {
  totalCandidateEnriched: number
  totalCandidateEnrichedLast90Days: number
  totalCandidateEnrichedLast180Days: number
}

export async function getProductName(): Promise<ProductNameResponse> {
  return request('GET', `${REPORTS}/getProductName`)
}

/**
 * ⚠️ TAKES SIXTY SECONDS, AND THAT IS NOT A HANG. Measured at 60.01s against the dev tenant,
 * repeatably. `@fastify/reply-from` abandons an upstream request after 10s by default, so
 * `apps/server` answered 504 at exactly 10.0s while the tenant was still working; the proxy
 * now allows 120s. Any caller needs a budget to match — a 30s capture timeout will not see
 * this screen finish.
 *
 * ⚠️ AND THE BODY MUST BE AN EXPLICIT `null`, NOT OMITTED. Upstream sends `data: null` with a
 * JSON content type, which is the only shape that works. Measured through the proxy:
 *
 *     no content-type, no body            → 415
 *     content-type json, no body          → 400  FST_ERR_CTP_EMPTY_JSON_BODY
 *     content-type json, body `null`      → 200
 *
 * `request()` sends no body and no content-type for `undefined`, and `"null"` with a JSON
 * content type for `null` — so the argument below is load-bearing and must not be "tidied".
 */
export async function generateEnrichmentReport(): Promise<EnrichmentReportResponse> {
  return request('POST', `${REPORTS}/generateEnrichmentReport`, null)
}

/**
 * Note the `Extended` suffix on the path — it is a different endpoint, not a variant spelling.
 *
 * Unlike the enrichment report this one is FAST (40ms against the dev tenant, three users), so
 * a screen that waits 60s is waiting on the enrichment half and nothing else.
 */
export async function generateUsageReport(report: UsageReportRequest): Promise<UsageReportResponse> {
  return request('POST', `${REPORTS}/generateUsageReportExtended`, report)
}

/**
 * `exportUsageReportToCsv` — upstream routes this through `FileDownload.saveFile`; `fetchFile`
 * is that helper's fetch half, and `packages/ui`'s `saveFile` its write half.
 *
 * `filenameSuffix` is a QUERY parameter while the request is the BODY — upstream passes it as
 * `params` alongside `data`, and the server composes the download name from it.
 */
export async function exportUsageReportToCsv(
  report: UsageReportRequest,
  filenameSuffix: string,
): Promise<DownloadedFile> {
  return fetchFile(`${REPORTS}/exportUsageReportToCsv?${new URLSearchParams({ filenameSuffix }).toString()}`, {
    method: 'POST',
    body: report,
    fallbackFilename: 'usage-report.csv',
    failureMessage: 'The export failed.',
  })
}

/**
 * `yyyy-MM-dd` in LOCAL time, which is what the CSV name carries. `toISOString()` would be
 * UTC and shifts the date by a day either side of midnight for most of the world.
 */
export function csvDateString(timestamp: number): string {
  const date = new Date(timestamp)
  const month = String(date.getMonth() + 1).padStart(2, '0')
  const day = String(date.getDate()).padStart(2, '0')
  return `${String(date.getFullYear())}-${month}-${day}`
}

/** `until <to>` when no from-date was chosen, `<from> to <to>` otherwise. */
export function csvFilenameSuffix(report: UsageReportRequest): string {
  if (report.fromTimestamp === undefined) return `until ${csvDateString(report.toTimestamp)}`
  return `${csvDateString(report.fromTimestamp)} to ${csvDateString(report.toTimestamp)}`
}
